import * as _ from "lodash";
import { Box, Button } from "@mui/joy";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { ProgramContentType } from "data/BlockComponent";
import { useRecoilState } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import { setProgramContentData } from "logic/logics";
import { captureException } from "utils/sentry";
import AudioProgressBar from "./AudioProgressBar";
import { theme } from "styles/theme";
import { activeAudioPlayerAtom } from "recoil/activeAudioPlayerAtom";
import { PauseIcon, PlayIcon } from "./AudioButtonStatus";
import { isPlaybackAtEnd } from "./audioReplayLogic";

// 진단용 모듈 변수: 같은 화면에 동시 마운트되는 인스턴스 수 추적 (H4 검증)
let mountedCount = 0;

// HTMLMediaError code 텍스트 매핑 (https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code)
const MEDIA_ERROR_CODE_TEXT: Record<number, string> = {
  1: "ABORTED",
  2: "NETWORK",
  3: "DECODE",
  4: "SRC_NOT_SUPPORTED",
};

const audioBreadcrumb = (message: string, data: Record<string, unknown>) => {
  try {
    Sentry.addBreadcrumb({
      category: "audio",
      level: "info",
      message,
      data,
    });
  } catch {
    // Sentry SDK 이슈로 사용자 흐름이 막히지 않도록 swallow
  }
};

const snapshotAudio = (el: HTMLAudioElement | null) => {
  let bufferedEnd: number | null = null;
  let bufferedRatio: number | null = null;
  try {
    const buffered = el?.buffered;
    if (buffered && buffered.length > 0) {
      bufferedEnd = buffered.end(buffered.length - 1);
      const d = el?.duration;
      if (typeof d === "number" && isFinite(d) && d > 0) {
        bufferedRatio = bufferedEnd / d;
      }
    }
  } catch {
    // 일부 브라우저는 메타데이터 로드 전 buffered 접근 시 throw 가능
  }
  return {
    readyState: el?.readyState,
    networkState: el?.networkState,
    paused: el?.paused,
    currentSrc: el?.currentSrc,
    errorCode: el?.error?.code,
    errorMessage: el?.error?.message,
    duration: el?.duration,
    currentTime: el?.currentTime,
    bufferedEnd,
    bufferedRatio,
  };
};

const audioFileTagFromUrl = (url?: string) =>
  url
    ?.split("/")
    .pop()
    ?.replace(/\.mp3$/, "") ?? "unknown";

// navigator.userActivation 스냅샷 (H7 user gesture 컨텍스트 검증).
// Safari iOS 16.4+ 지원. 미지원 환경은 undefined 로 graceful degrade.
type NavWithActivation = Navigator & {
  userActivation?: { isActive?: boolean; hasBeenActive?: boolean };
};
const snapshotUserActivation = () => {
  const ua = (navigator as NavWithActivation).userActivation;
  return {
    isActive: ua?.isActive,
    hasBeenActive: ua?.hasBeenActive,
  };
};

export default function AudioButtonWeb({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl,
  extraLoopNumber, // 안씀
  readonly,
  canReplay,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  extraLoopNumber?: number; // 안씀
  readonly: boolean;
  canReplay?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const extraAudioRef = useRef<HTMLAudioElement>(null);

  const animationFrameRef = useRef<number | null>(null);
  const liveSessionStartTimeRef = useRef<number | null>(null);
  const liveSessionStartCurrentTimeRef = useRef<number | null>(null);

  // 진단용 ref (H1~H4, H7 가설 검증)
  const lastStartAtRef = useRef<number | null>(null);
  const lastSeekStartAtRef = useRef<number | null>(null);
  const exceptionSentRef = useRef(false);

  // 다시 재생 직후 loadeddata가 stale initialTime을 재적용해 끝으로 스냅백하는 것 방지
  // (코치는 content를 쓰지 않아 initialTime prop이 0으로 갱신되지 않음)
  const replayRequestedRef = useRef(false);

  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [isSeeking, setIsSeeking] = useState(false);

  const [activeAudioKey, setActiveAudioKey] = useRecoilState(
    activeAudioPlayerAtom
  );
  const isPlaying = activeAudioKey === audioInstanceKey;

  const isLive = !!extraAudioUrl && duration > 0 && currentTime >= duration;

  const showReplay =
    !!canReplay && !isPlaying && isPlaybackAtEnd(duration, currentTime);

  // 진단: 마운트/언마운트 시점에 동시 인스턴스 수와 함께 기록 (H4 검증)
  useEffect(() => {
    mountedCount += 1;
    audioBreadcrumb("mounted", {
      audioInstanceKey,
      audioUrl,
      mountedCount,
    });
    return () => {
      mountedCount = Math.max(0, mountedCount - 1);
      audioBreadcrumb("unmounted", {
        audioInstanceKey,
        mountedCount,
      });
    };
  }, [audioInstanceKey, audioUrl]);

  // 진단: activeAudioKey 변화 추적. 자기 인스턴스에 영향을 주는 변화만 (in/out)
  // breadcrumb 으로 캡처하여 12개 동시 마운트 환경에서 noise 최소화.
  const prevActiveAudioKeyRef = useRef<string | null>(activeAudioKey);
  useEffect(() => {
    const prev = prevActiveAudioKeyRef.current;
    if (prev !== activeAudioKey) {
      const myKeyAffected =
        prev === audioInstanceKey || activeAudioKey === audioInstanceKey;
      if (myKeyAffected) {
        audioBreadcrumb("active_key_changed", {
          observedBy: audioInstanceKey,
          prev,
          next: activeAudioKey,
          isMyKeyNow: activeAudioKey === audioInstanceKey,
          msSinceLastStart:
            lastStartAtRef.current != null
              ? Date.now() - lastStartAtRef.current
              : null,
        });
      }
      prevActiveAudioKeyRef.current = activeAudioKey;
    }
  }, [activeAudioKey, audioInstanceKey]);

  const pauseAllAudio = useCallback(() => {
    audioRef.current?.pause();
    extraAudioRef.current?.pause();
  }, []);

  const resetLiveSessionTimer = useCallback(() => {
    liveSessionStartTimeRef.current = null;
    liveSessionStartCurrentTimeRef.current = null;
  }, []);

  const clearActivePlayer = useCallback(() => {
    setActiveAudioKey((currentKey) =>
      currentKey === audioInstanceKey ? null : currentKey
    );
  }, [audioInstanceKey, setActiveAudioKey]);

  const getPersistedCurrentTime = useCallback(() => {
    if (isLive) {
      return currentTime;
    }
    return audioRef.current?.currentTime ?? currentTime;
  }, [currentTime, isLive]);

  useEffect(() => {
    if (isPlaying) {
      return;
    }

    setCurrentTime(initialTime);
    if (!audioRef.current) return;

    if (initialTime >= duration && duration > 0) {
      audioRef.current.currentTime = duration;
      if (extraAudioRef.current) {
        extraAudioRef.current.currentTime = 0;
      }
    } else {
      audioRef.current.currentTime = initialTime;
      if (extraAudioRef.current) {
        extraAudioRef.current.currentTime = 0;
      }
    }
  }, [initialTime, duration, isPlaying]);

  const updateCurrentTimeData = useCallback(
    (timeToUpdate?: number) => {
      const updatedTime = timeToUpdate ?? currentTime;
      const isTextareaFocused = document.activeElement?.tagName === "TEXTAREA";
      const isEnded =
        duration > 0 &&
        updatedTime > 0 &&
        duration !== updatedTime &&
        duration - 30 <= updatedTime;

      if (!isTextareaFocused || isEnded) {
        setProgramContentData({
          setData,
          blockIndex,
          lineIndex,
          cellIndex,
          newlyAddedData: {
            currentTime: updatedTime,
            ...(updatedTime > 0 && {
              id: "meditation_time",
              value: `${Math.floor((updatedTime + 10 || 60) / 60)}`,
            }),
            ...(isEnded && {
              isEnded: true,
            }),
          },
        });
      }
    },
    [currentTime, duration, setData, blockIndex, lineIndex, cellIndex]
  );

  useEffect(() => {
    if (isPlaying && currentTime > 0 && Math.floor(currentTime) % 5 === 0) {
      updateCurrentTimeData();
    }
  }, [currentTime, isPlaying, updateCurrentTimeData]);

  useEffect(() => {
    const stopTimer = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    if (isPlaying && isLive && !isSeeking) {
      const tick = () => {
        if (
          liveSessionStartTimeRef.current === null ||
          liveSessionStartCurrentTimeRef.current === null
        ) {
          liveSessionStartTimeRef.current = performance.now();
          liveSessionStartCurrentTimeRef.current = currentTime;
        }

        const elapsed = performance.now() - liveSessionStartTimeRef.current;
        const newTotalTime =
          liveSessionStartCurrentTimeRef.current + elapsed / 1000;

        setCurrentTime(newTotalTime);

        animationFrameRef.current = requestAnimationFrame(tick);
      };

      stopTimer();
      tick();
    } else {
      stopTimer();
    }

    return stopTimer;
  }, [isPlaying, isLive, currentTime, duration, isSeeking]);

  const handlePlaybackException = useCallback(
    (source: string, sourceUrl: string | undefined, error?: unknown) => {
      const playbackTime = getPersistedCurrentTime();
      console.error(
        `[AudioButtonWeb ${audioInstanceKey}] ${source} playback failed.`,
        {
          playbackTime,
          sourceUrl,
          error,
        }
      );
      // Sentry: 인스턴스당 1회만 발송하여 폭주/quota 보호
      if (!exceptionSentRef.current) {
        exceptionSentRef.current = true;
        const errObj =
          error instanceof Error
            ? error
            : new Error(
                typeof error === "string" ? error : "audio playback failed"
              );
        captureException(errObj, {
          level: "warning",
          tags: {
            feature: "meditation_audio",
            source,
            errorName: errObj.name,
            audioFile: audioFileTagFromUrl(sourceUrl),
          },
          extra: {
            audioInstanceKey,
            sourceUrl,
            playbackTime,
            mainSnapshot: snapshotAudio(audioRef.current),
            extraSnapshot: snapshotAudio(extraAudioRef.current),
            mountedCount,
            msSinceLastStart:
              lastStartAtRef.current != null
                ? Date.now() - lastStartAtRef.current
                : null,
            userActivation: snapshotUserActivation(),
          },
        });
      }
      pauseAllAudio();
      resetLiveSessionTimer();
      updateCurrentTimeData(playbackTime);
      audioBreadcrumb("clear_active_player_called", {
        audioInstanceKey,
        caller: "playback_exception",
      });
      clearActivePlayer();
    },
    [
      audioInstanceKey,
      clearActivePlayer,
      getPersistedCurrentTime,
      pauseAllAudio,
      resetLiveSessionTimer,
      updateCurrentTimeData,
    ]
  );

  const getTargetAudioElement = useCallback(() => {
    const mainAudio = audioRef.current;
    if (!mainAudio) {
      return null;
    }

    const shouldPlayExtra =
      !!extraAudioRef.current &&
      duration > 0 &&
      (isLive || mainAudio.currentTime >= duration);

    return shouldPlayExtra ? extraAudioRef.current : mainAudio;
  }, [duration, isLive]);

  const playAudioElement = useCallback(
    (
      audioElement: HTMLAudioElement | null,
      source: string,
      sourceUrl: string | undefined
    ) => {
      if (!audioElement) {
        handlePlaybackException(
          source,
          sourceUrl,
          new Error("Audio element is not available")
        );
        return;
      }

      // L2: .play() 호출 직전 상태 스냅샷 + userActivation (H7)
      const playInvokedAt = Date.now();
      audioBreadcrumb("play_invoked", {
        audioInstanceKey,
        source,
        sourceUrl,
        snapshot: snapshotAudio(audioElement),
        userActivation: snapshotUserActivation(),
      });

      const playPromise = audioElement.play();
      if (playPromise !== undefined) {
        // 기존 동작: 실패 시 fallback 처리. 사용자 흐름에 영향을 주는 유일한 chain.
        playPromise.catch((error) => {
          handlePlaybackException(source, sourceUrl, error);
        });
        // 진단용 별도 chain: .then 콜백의 어떤 throw 도 위 .catch 와 격리됨.
        // onRejected 도 swallow 하여 unhandled rejection 방지.
        playPromise.then(
          () => {
            try {
              // L3: .then 내부에서 .play() 등 흐름 변경 작업은 절대 하지 않음
              audioBreadcrumb("play_resolved", {
                audioInstanceKey,
                source,
                elapsedMs: Date.now() - playInvokedAt,
                snapshot: snapshotAudio(audioElement),
              });
            } catch {
              // 로깅 실패는 무시
            }
          },
          () => {
            // .catch chain 에서 이미 처리됨. 여기는 noop.
          }
        );
      }
    },
    [audioInstanceKey, handlePlaybackException]
  );

  const start = useCallback(() => {
    // L1: start() 진입 시점 진단 (H1/H3 검증: readyState, duration, currentTime)
    lastStartAtRef.current = Date.now();
    exceptionSentRef.current = false;
    audioBreadcrumb("start_called", {
      audioInstanceKey,
      audioUrl,
      isLive,
      currentTime,
      duration,
      activeAudioKey,
      mainSnapshot: snapshotAudio(audioRef.current),
      extraSnapshot: snapshotAudio(extraAudioRef.current),
      userActivation: snapshotUserActivation(),
    });
    resetLiveSessionTimer();
    setActiveAudioKey(audioInstanceKey);
    playAudioElement(
      getTargetAudioElement(),
      "start",
      isLive ? extraAudioUrl : audioUrl
    );
  }, [
    audioInstanceKey,
    audioUrl,
    extraAudioUrl,
    getTargetAudioElement,
    isLive,
    playAudioElement,
    resetLiveSessionTimer,
    setActiveAudioKey,
    activeAudioKey,
    currentTime,
    duration,
  ]);

  // 다시주기 상태에서 끝까지 들은 오디오를 처음부터 다시 재생
  const replay = useCallback(() => {
    replayRequestedRef.current = true;
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    setCurrentTime(0);
    if (!isCoach) {
      // initialTime prop을 0으로 갱신해 비재생 동기화 effect의 스냅백 방지.
      // merge 방식이라 isEnded·meditation_time은 보존됨 (완료 게이팅 불변).
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: { currentTime: 0 },
      });
    }
    start();
  }, [isCoach, setData, blockIndex, lineIndex, cellIndex, start]);

  const stop = useCallback(
    (timeToPersist?: number) => {
      replayRequestedRef.current = false;
      pauseAllAudio();
      resetLiveSessionTimer();
      updateCurrentTimeData(timeToPersist ?? getPersistedCurrentTime());
      audioBreadcrumb("clear_active_player_called", {
        audioInstanceKey,
        caller: "stop",
      });
      clearActivePlayer();
    },
    [
      clearActivePlayer,
      getPersistedCurrentTime,
      pauseAllAudio,
      resetLiveSessionTimer,
      updateCurrentTimeData,
      audioInstanceKey,
    ]
  );

  useEffect(() => {
    if (!isPlaying) {
      // L6: 직전 start() 로부터 짧은 시간 안에 pause 가 호출되면 H2(race) 의심
      if (lastStartAtRef.current != null) {
        const msSinceStart = Date.now() - lastStartAtRef.current;
        if (msSinceStart < 2000) {
          audioBreadcrumb("isPlaying_off_pause_called", {
            audioInstanceKey,
            msSinceStart,
            mainSnapshot: snapshotAudio(audioRef.current),
          });
        }
      }
      pauseAllAudio();
      resetLiveSessionTimer();
    }
  }, [isPlaying, pauseAllAudio, resetLiveSessionTimer, audioInstanceKey]);

  useEffect(() => {
    return () => {
      audioBreadcrumb("clear_active_player_called", {
        audioInstanceKey,
        caller: "unmount_cleanup",
      });
      clearActivePlayer();
    };
  }, [clearActivePlayer, audioInstanceKey]);

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  const handleEnded = useCallback(() => {
    setCurrentTime(duration);

    if (extraAudioUrl && isPlaying) {
      resetLiveSessionTimer();
      updateCurrentTimeData(duration);
      if (!isCoach) {
        setAudioEnded();
      }
      playAudioElement(extraAudioRef.current, "extra", extraAudioUrl);
    } else {
      stop(duration);
      if (!isCoach) {
        setAudioEnded();
      }
    }
  }, [
    duration,
    extraAudioUrl,
    extraAudioRef,
    isPlaying,
    isCoach,
    playAudioElement,
    resetLiveSessionTimer,
    setAudioEnded,
    stop,
    updateCurrentTimeData,
  ]);

  const handleDurationChange = useCallback(() => {
    if (audioRef.current?.duration) {
      audioBreadcrumb("duration_change", {
        audioInstanceKey,
        duration: audioRef.current.duration,
        msSinceLastStart:
          lastStartAtRef.current != null
            ? Date.now() - lastStartAtRef.current
            : null,
      });
      setDuration(audioRef.current.duration);
    }
  }, [audioInstanceKey]);

  const handleLoadedData = useCallback(() => {
    if (audioRef.current) {
      audioBreadcrumb("loaded_data", {
        audioInstanceKey,
        duration: audioRef.current.duration,
        msSinceLastStart:
          lastStartAtRef.current != null
            ? Date.now() - lastStartAtRef.current
            : null,
        snapshot: snapshotAudio(audioRef.current),
      });
      // 다시 재생 중이면 stale initialTime 대신 0을 유지 (Safari는 play() 후 loadeddata 발화)
      const resumeTime = replayRequestedRef.current ? 0 : initialTime;
      audioRef.current.currentTime = resumeTime;
      setCurrentTime(resumeTime);
      if (audioRef.current.duration) {
        setDuration(audioRef.current.duration);
      }
    }
  }, [initialTime, audioInstanceKey]);

  const handleTimeUpdate = useCallback(() => {
    if (isSeeking || isLive) return;
    if (audioRef.current) {
      const newCurrentTime = audioRef.current.currentTime;
      setCurrentTime(newCurrentTime);
      if (
        !isCoach &&
        newCurrentTime > 0 &&
        duration > 0 &&
        newCurrentTime >= duration
      ) {
        setAudioEnded();
      }
    }
  }, [isSeeking, isLive, isCoach, duration, setAudioEnded]);

  const handleSeekStart = useCallback(() => {
    // L10: 시크 시작 시점 + 직전 start_called 까지의 시간차 (H7 race 검증)
    lastSeekStartAtRef.current = Date.now();
    audioBreadcrumb("seek_start", {
      audioInstanceKey,
      isPlaying,
      msSinceLastStart:
        lastStartAtRef.current != null
          ? Date.now() - lastStartAtRef.current
          : null,
      mainSnapshot: snapshotAudio(audioRef.current),
      userActivation: snapshotUserActivation(),
    });
    setIsSeeking(true);
    pauseAllAudio();
  }, [pauseAllAudio, audioInstanceKey, isPlaying]);

  const handleSeekChange = useCallback((newTime: number) => {
    // While dragging, just update the visual time
    setCurrentTime(newTime);
  }, []);

  const handleSeekEnd = useCallback(
    (newTime: number) => {
      // L11: 시크 종료 시점 + willCallPlay + 직전 seek_start 와의 시간차 (H7 검증)
      audioBreadcrumb("seek_end", {
        audioInstanceKey,
        newTime,
        isPlaying,
        willCallPlay: isPlaying,
        mainPausedBefore: audioRef.current?.paused,
        msSinceSeekStart:
          lastSeekStartAtRef.current != null
            ? Date.now() - lastSeekStartAtRef.current
            : null,
        userActivation: snapshotUserActivation(),
      });
      resetLiveSessionTimer();

      if (audioRef.current) {
        if (newTime >= duration && duration > 0) {
          audioRef.current.currentTime = duration;
        } else {
          audioRef.current.currentTime = newTime;
        }
      }
      setCurrentTime(newTime);

      if (isPlaying) {
        playAudioElement(
          getTargetAudioElement(),
          "seek",
          newTime >= duration && duration > 0 ? extraAudioUrl : audioUrl
        );
      }

      setIsSeeking(false);
    },
    [
      audioUrl,
      duration,
      extraAudioUrl,
      getTargetAudioElement,
      isPlaying,
      playAudioElement,
      resetLiveSessionTimer,
      audioInstanceKey,
    ]
  );

  const [permanantDataState, setPermanantDataState] =
    useRecoilState(permanentDataAtom);

  const timeSpentInBackgroundEvent = useGetCertainEventFromApp({
    eventName: "timeSpentInBackground",
  });

  useEffect(() => {
    if (timeSpentInBackgroundEvent) {
      const seconds = timeSpentInBackgroundEvent.params.seconds;
      const stored = permanantDataState?.audioCurrentTime;
      if (!isPlaying && stored?.audioInstanceKey === audioInstanceKey) {
        setCurrentTime(stored.currentTime + seconds);
      }
    }
  }, [
    timeSpentInBackgroundEvent,
    isPlaying,
    permanantDataState,
    audioInstanceKey,
  ]);

  const handleVisibilityChange = useCallback(() => {
    // L7: 백그라운드 ↔ 포그라운드 전환 추적 (H1/H6 검증)
    audioBreadcrumb("visibility_change", {
      audioInstanceKey,
      hidden: document.hidden,
      isPlaying,
      mainSnapshot: snapshotAudio(audioRef.current),
    });
    if (document.hidden) {
      // 숨겨진 분기(display:none)에도 mount되는 다른 인스턴스들이 키를 덮어쓰지 않도록,
      // 실제 재생 중인 플레이어만 백그라운드 이어듣기 상태를 저장한다.
      if (isPlaying) {
        const playbackTime = getPersistedCurrentTime();
        setPermanantDataState((state) => ({
          ...state,
          audioCurrentTime: { audioInstanceKey, currentTime: playbackTime },
        }));
      }
      if (!isCoach) setAudioEnded();
    }
  }, [
    isPlaying,
    audioInstanceKey,
    getPersistedCurrentTime,
    isCoach,
    setAudioEnded,
    setPermanantDataState,
  ]);

  const handleMainAudioError = useCallback(() => {
    const errorCode = audioRef.current?.error?.code;
    const errorMessage = audioRef.current?.error?.message;
    // L13: media error 발생 시 어느 자산이 실패했는지 명시적으로 breadcrumb
    audioBreadcrumb("media_error_event", {
      source: "main",
      audioFile: audioFileTagFromUrl(audioUrl),
      errorCode,
      errorCodeText: MEDIA_ERROR_CODE_TEXT[errorCode ?? -1] ?? "UNKNOWN",
      errorMessage,
      audioUrl,
    });
    handlePlaybackException(
      "main audio",
      audioUrl,
      new Error(
        `HTMLMediaElement error: ${MEDIA_ERROR_CODE_TEXT[errorCode ?? -1] ?? "UNKNOWN"} (code=${errorCode ?? "unknown"})${
          errorMessage ? `: ${errorMessage}` : ""
        }`
      )
    );
  }, [audioUrl, handlePlaybackException]);

  const handleExtraAudioError = useCallback(() => {
    if (!extraAudioUrl) {
      return;
    }
    const errorCode = extraAudioRef.current?.error?.code;
    const errorMessage = extraAudioRef.current?.error?.message;
    audioBreadcrumb("media_error_event", {
      source: "extra",
      audioFile: audioFileTagFromUrl(extraAudioUrl),
      errorCode,
      errorCodeText: MEDIA_ERROR_CODE_TEXT[errorCode ?? -1] ?? "UNKNOWN",
      errorMessage,
      audioUrl: extraAudioUrl,
    });
    handlePlaybackException(
      "extra audio",
      extraAudioUrl,
      new Error(
        `HTMLMediaElement error: ${MEDIA_ERROR_CODE_TEXT[errorCode ?? -1] ?? "UNKNOWN"} (code=${errorCode ?? "unknown"})${
          errorMessage ? `: ${errorMessage}` : ""
        }`
      )
    );
  }, [extraAudioUrl, handlePlaybackException]);

  useEffect(() => {
    const audioEl = audioRef.current;
    const extraAudioEl = extraAudioRef.current;

    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (audioEl) {
      audioEl.addEventListener("loadeddata", handleLoadedData);
      audioEl.addEventListener("durationchange", handleDurationChange);
      audioEl.addEventListener("timeupdate", handleTimeUpdate);
      audioEl.addEventListener("ended", handleEnded);
      audioEl.addEventListener("error", handleMainAudioError);
    }
    if (extraAudioEl) {
      extraAudioEl.addEventListener("error", handleExtraAudioError);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (audioEl) {
        audioEl.removeEventListener("loadeddata", handleLoadedData);
        audioEl.removeEventListener("durationchange", handleDurationChange);
        audioEl.removeEventListener("timeupdate", handleTimeUpdate);
        audioEl.removeEventListener("ended", handleEnded);
        audioEl.removeEventListener("error", handleMainAudioError);
      }
      if (extraAudioEl) {
        extraAudioEl.removeEventListener("error", handleExtraAudioError);
      }
    };
  }, [
    handleExtraAudioError,
    handleVisibilityChange,
    handleLoadedData,
    handleDurationChange,
    handleTimeUpdate,
    handleEnded,
    handleMainAudioError,
  ]);

  // 진단: 네트워크/디코더 fetch 상태 (H1/H9 검증).
  // progress 는 500ms throttle, stalled/suspend/waiting 은 그대로 캡처.
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    let lastProgressLogAt = 0;
    const onProgress = () => {
      const now = Date.now();
      if (now - lastProgressLogAt < 500) return;
      lastProgressLogAt = now;
      audioBreadcrumb("audio_progress", {
        audioInstanceKey,
        snapshot: snapshotAudio(audioEl),
      });
    };
    const onStalled = () =>
      audioBreadcrumb("audio_stalled", {
        audioInstanceKey,
        snapshot: snapshotAudio(audioEl),
      });
    const onSuspend = () =>
      audioBreadcrumb("audio_suspend", {
        audioInstanceKey,
        snapshot: snapshotAudio(audioEl),
      });
    const onWaiting = () =>
      audioBreadcrumb("audio_waiting", {
        audioInstanceKey,
        snapshot: snapshotAudio(audioEl),
      });

    audioEl.addEventListener("progress", onProgress);
    audioEl.addEventListener("stalled", onStalled);
    audioEl.addEventListener("suspend", onSuspend);
    audioEl.addEventListener("waiting", onWaiting);

    return () => {
      audioEl.removeEventListener("progress", onProgress);
      audioEl.removeEventListener("stalled", onStalled);
      audioEl.removeEventListener("suspend", onSuspend);
      audioEl.removeEventListener("waiting", onWaiting);
    };
  }, [audioInstanceKey]);

  const startRef = useRef(start);
  const stopRef = useRef(stop);

  useEffect(() => {
    startRef.current = start;
    stopRef.current = stop;
  }, [start, stop]);

  // ========================[ 수정된 부분 시작 ]========================
  // 이 useEffect는 현재 오디오 플레이어가 활성화되었을 때 MediaSession을 관리합니다.
  useEffect(() => {
    if (activeAudioKey === audioInstanceKey) {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: title || "명상훈련",
          artist: artist || "디스턴싱",
          artwork: artwork
            ? [
                { src: artwork, sizes: "96x96", type: "image/png" },
                { src: artwork, sizes: "512x512", type: "image/png" },
              ]
            : [],
        });

        navigator.mediaSession.setActionHandler("play", () =>
          startRef.current()
        );
        navigator.mediaSession.setActionHandler("pause", () =>
          stopRef.current()
        );
      }

      // cleanup 함수: 플레이어가 비활성화될 때(일시정지 또는 다른 플레이어 재생) 호출됩니다.
      // 'pause' 핸들러만 제거하고 'play' 핸들러는 남겨두어, 미디어 컨트롤에서 다시 재생할 수 있도록 합니다.
      return () => {
        if ("mediaSession" in navigator) {
          if (
            navigator.mediaSession.metadata?.title === (title || "명상훈련")
          ) {
            navigator.mediaSession.setActionHandler("pause", null);
          }
        }
      };
    }
  }, [activeAudioKey, audioInstanceKey, title, artist, artwork]);

  // 이 useEffect는 컴포넌트가 unmount될 때 최종 정리를 담당합니다.
  // 메모리 누수 및 unmount된 컴포넌트의 상태를 변경하려는 시도를 방지합니다.
  useEffect(() => {
    // 컴포넌트가 생성될 때의 title 값을 저장합니다.
    const currentTitle = title || "명상훈련";
    return () => {
      if ("mediaSession" in navigator) {
        // unmount 시점에, MediaSession의 소유자가 여전히 이 컴포넌트인 경우에만 핸들러를 제거합니다.
        // 이를 통해 다른 활성 플레이어의 핸들러를 실수로 제거하는 것을 방지합니다.
        if (navigator.mediaSession.metadata?.title === currentTitle) {
          navigator.mediaSession.setActionHandler("play", null);
          navigator.mediaSession.setActionHandler("pause", null);
        }
      }
    };
  }, [title]); // title이 변경될 수 있으므로 의존성 배열에 추가합니다. []도 가능합니다.
  // ========================[ 수정된 부분 끝 ]========================

  return (
    <Box>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        style={{ display: "none" }}
        data-testid="audio-main"
      ></audio>
      {extraAudioUrl && (
        <audio
          ref={extraAudioRef}
          src={extraAudioUrl}
          loop
          preload="none"
          style={{ display: "none" }}
        ></audio>
      )}

      <AudioProgressBar
        currentTime={currentTime}
        duration={duration}
        onSeekStart={handleSeekStart}
        onSeekChange={handleSeekChange}
        onSeekEnd={handleSeekEnd}
        isPlaying={isPlaying}
        isLive={isLive}
      />

      <Button
        key={`${audioInstanceKey}_${extraAudioUrl || "noExtraAudioUrl"}`}
        variant={isPlaying ? "Outlined" : "Solid"}
        color="primary"
        fullWidth
        onClick={() => {
          if (isPlaying) {
            stop();
          } else if (showReplay) {
            replay();
          } else {
            start();
          }
        }}
        size={"lg"}
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          justifyContent: "center",
          "& .MuiButton-startDecorator": {
            marginRight: 0,
            color: readonly
              ? theme.vars.palette.Content.SuperWeak
              : isPlaying
                ? theme.vars.palette.Content.Highlight
                : "white",
          },
          gap: 1,
        }}
        disabled={readonly}
        startDecorator={isPlaying ? <PauseIcon /> : <PlayIcon />}
      >
        {isPlaying ? "일시정지" : showReplay ? "다시 재생하기" : "재생하기"}
      </Button>
    </Box>
  );
}
